import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { 
  Globe, 
  BookOpen, 
  Loader2, 
  CheckCircle2, 
  XCircle, 
  RefreshCw,
  Search,
  HelpCircle,
  Users
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface GenerationResult {
  moduleId: string;
  title: string;
  status: 'success' | 'error';
  contentSections?: number;
  quizQuestions?: number;
  error?: string;
}

const ContentGeneration = () => {
  const { language } = useLanguage();
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<GenerationResult[]>([]);
  const [currentModule, setCurrentModule] = useState<string>('');

  const generateContent = async () => {
    setIsGenerating(true);
    setProgress(0);
    setResults([]);
    setCurrentModule('');

    try {
      toast.success(
        language === 'en' 
          ? 'Starting content generation...' 
          : 'Ξεκινά η δημιουργία περιεχομένου...'
      );

      const { data, error } = await supabase.functions.invoke('generate-training-content');

      if (error) throw error;

      console.log('Content generation response:', data);

      if (data.success) {
        setResults(data.results || []);
        setProgress(100);
        
        toast.success(
          language === 'en' 
            ? `Content generated successfully! ${data.successfulUpdates}/${data.totalModules} modules updated.`
            : `Το περιεχόμενο δημιουργήθηκε επιτυχώς! ${data.successfulUpdates}/${data.totalModules} modules ενημερώθηκαν.`
        );
      } else {
        throw new Error(data.error || 'Content generation failed');
      }

    } catch (error: any) {
      console.error('Error generating content:', error);
      toast.error(
        language === 'en' 
          ? `Failed to generate content: ${error.message}`
          : `Αποτυχία δημιουργίας περιεχομένου: ${error.message}`
      );
    } finally {
      setIsGenerating(false);
      setCurrentModule('');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'error':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
        return 'bg-green-50 border-green-200';
      case 'error':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-blue-50 border-blue-200';
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">
            {language === 'en' ? 'Training Content Generation' : 'Δημιουργία Εκπαιδευτικού Περιεχομένου'}
          </h1>
          <p className="text-muted-foreground mt-2">
            {language === 'en' 
              ? 'Generate comprehensive educational content and quizzes for all training modules using AI and web research'
              : 'Δημιουργήστε ολοκληρωμένο εκπαιδευτικό περιεχόμενο και κουίζ για όλα τα training modules χρησιμοποιώντας AI και διαδικτυακή έρευνα'
            }
          </p>
        </div>
      </div>

      {/* Generation Control */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Globe className="h-5 w-5 mr-2 text-blue-600" />
            {language === 'en' ? 'Content Generation' : 'Δημιουργία Περιεχομένου'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center p-4 bg-blue-50 rounded-lg">
              <Search className="h-8 w-8 text-blue-600 mr-3" />
              <div>
                <h3 className="font-medium">
                  {language === 'en' ? 'Web Research' : 'Διαδικτυακή Έρευνα'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {language === 'en' ? 'Find latest educational content' : 'Εύρεση νεότερου εκπαιδευτικού υλικού'}
                </p>
              </div>
            </div>
            
            <div className="flex items-center p-4 bg-green-50 rounded-lg">
              <BookOpen className="h-8 w-8 text-green-600 mr-3" />
              <div>
                <h3 className="font-medium">
                  {language === 'en' ? 'Content Creation' : 'Δημιουργία Περιεχομένου'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {language === 'en' ? 'AI-generated lessons & exercises' : 'Μαθήματα & ασκήσεις από AI'}
                </p>
              </div>
            </div>
            
            <div className="flex items-center p-4 bg-purple-50 rounded-lg">
              <HelpCircle className="h-8 w-8 text-purple-600 mr-3" />
              <div>
                <h3 className="font-medium">
                  {language === 'en' ? 'Quiz Generation' : 'Δημιουργία Κουίζ'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {language === 'en' ? 'Assessment questions & answers' : 'Ερωτήσεις αξιολόγησης & απαντήσεις'}
                </p>
              </div>
            </div>
          </div>

          {isGenerating && (
            <div className="space-y-4">
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>
                  {language === 'en' ? 'Generating content...' : 'Δημιουργία περιεχομένου...'}
                </span>
                <span>{Math.round(progress)}%</span>
              </div>
              <Progress value={progress} className="h-2" />
              {currentModule && (
                <p className="text-sm text-blue-600">
                  {language === 'en' ? 'Processing: ' : 'Επεξεργασία: '}{currentModule}
                </p>
              )}
            </div>
          )}

          <div className="flex justify-center">
            <Button 
              onClick={generateContent}
              disabled={isGenerating}
              size="lg"
              className="px-8"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {language === 'en' ? 'Generating...' : 'Δημιουργία...'}
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  {language === 'en' ? 'Generate All Content' : 'Δημιουργία Όλου του Περιεχομένου'}
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {results.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Users className="h-5 w-5 mr-2 text-green-600" />
              {language === 'en' ? 'Generation Results' : 'Αποτελέσματα Δημιουργίας'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {results.map((result, index) => (
                <div
                  key={result.moduleId}
                  className={`p-4 rounded-lg border ${getStatusColor(result.status)}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      {getStatusIcon(result.status)}
                      <div>
                        <h4 className="font-medium">{result.title}</h4>
                        {result.status === 'success' && (
                          <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                            <span>
                              {language === 'en' ? 'Sections: ' : 'Ενότητες: '}{result.contentSections}
                            </span>
                            <span>
                              {language === 'en' ? 'Quiz Questions: ' : 'Ερωτήσεις: '}{result.quizQuestions}
                            </span>
                          </div>
                        )}
                        {result.status === 'error' && result.error && (
                          <p className="text-sm text-red-600">{result.error}</p>
                        )}
                      </div>
                    </div>
                    <Badge 
                      variant={result.status === 'success' ? 'default' : 'destructive'}
                    >
                      {result.status === 'success' 
                        ? (language === 'en' ? 'Success' : 'Επιτυχία')
                        : (language === 'en' ? 'Error' : 'Σφάλμα')
                      }
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-6 pt-4 border-t">
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>
                  {language === 'en' ? 'Total Modules: ' : 'Συνολικά Modules: '}
                  {results.length}
                </span>
                <span>
                  {language === 'en' ? 'Successful: ' : 'Επιτυχή: '}
                  {results.filter(r => r.status === 'success').length}
                </span>
                <span>
                  {language === 'en' ? 'Failed: ' : 'Αποτυχημένα: '}
                  {results.filter(r => r.status === 'error').length}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Information */}
      <Card>
        <CardHeader>
          <CardTitle>
            {language === 'en' ? 'How it Works' : 'Πώς Λειτουργεί'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="prose max-w-none">
            <ol className="space-y-2">
              <li>
                {language === 'en' 
                  ? '🔍 The system searches the internet for the latest educational content related to each training module'
                  : '🔍 Το σύστημα αναζητά στο διαδίκτυο το πιο πρόσφατο εκπαιδευτικό υλικό σχετικά με κάθε training module'
                }
              </li>
              <li>
                {language === 'en'
                  ? '🤖 AI analyzes the found content and creates structured lessons, practical exercises, and presentations'
                  : '🤖 Το AI αναλύει το υλικό που βρέθηκε και δημιουργεί δομημένα μαθήματα, πρακτικές ασκήσεις και παρουσιάσεις'
                }
              </li>
              <li>
                {language === 'en'
                  ? '❓ Comprehensive quizzes are generated with multiple-choice questions to test understanding'
                  : '❓ Δημιουργούνται ολοκληρωμένα κουίζ με ερωτήσεις πολλαπλής επιλογής για έλεγχο κατανόησης'
                }
              </li>
              <li>
                {language === 'en'
                  ? '🌐 All content is created in both English and Greek for bilingual support'
                  : '🌐 Όλο το περιεχόμενο δημιουργείται στα Αγγλικά και Ελληνικά για διγλωσσική υποστήριξη'
                }
              </li>
              <li>
                {language === 'en'
                  ? '💾 The generated content is automatically saved to the database and becomes immediately available'
                  : '💾 Το περιεχόμενο που δημιουργείται αποθηκεύεται αυτόματα στη βάση δεδομένων και γίνεται άμεσα διαθέσιμο'
                }
              </li>
            </ol>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ContentGeneration;